import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import AgoraRTC, { AgoraRTCProvider } from 'agora-rtc-react';
import { useLocation } from 'react-router-dom';
import { FloatingCallOverlay } from './FloatingCallOverlay';
import { CallMediaEngine } from './callMediaEngine';

/**
 * @typedef {Object} CallSession
 * @property {string} appId
 * @property {string} channel
 * @property {string} token
 * @property {number} uid
 * @property {string} joinCloseAt
 * @property {string} [joinOpenAt]
 * @property {string} [appointmentId]
 * @property {number} [serverOffsetMs]
 * @property {boolean} isMinimized
 */

export const CALL_CHAT_PATH = '/appointment/chat';

export const CallSessionContext = createContext(null);

export const useCallSession = () => useContext(CallSessionContext);

export const CallSessionProvider = ({ children }) => {
  const location = useLocation();
  const [callSession, setCallSession] = useState(null);
  const rtcClientRef = useRef(null);
  const teardownRef = useRef(null);
  const navigateAwayRef = useRef(null);

  const startCall = useCallback((callData) => {
    // Always create a fresh client per session (never reuse a left client).
    if (rtcClientRef.current) {
      try {
        rtcClientRef.current.leave().catch(() => {});
      } catch (_) {}
      rtcClientRef.current = null;
    }
    rtcClientRef.current = AgoraRTC.createClient({ codec: 'vp8', mode: 'rtc' });
    const serverNowMs = callData.serverNow ? new Date(callData.serverNow).getTime() : NaN;
    const serverOffsetMs = Number.isFinite(serverNowMs) ? serverNowMs - Date.now() : 0;
    setCallSession({
      appId: callData.appId || process.env.REACT_APP_AGORA_APP_ID || '8f19b88a53264105a373199ee57f10cf',
      channel: callData.channel,
      token: callData.token,
      uid: callData.uid,
      joinCloseAt: callData.joinCloseAt,
      joinOpenAt: callData.joinOpenAt || null,
      appointmentId: callData.appointmentId || null,
      serverOffsetMs,
      isMinimized: false,
    });
  }, []);

  const clearSession = useCallback(() => {
    setCallSession(null);
    rtcClientRef.current = null;
  }, []);

  const endCall = useCallback(async () => {
    try {
      if (teardownRef.current) {
        await teardownRef.current();
      } else if (rtcClientRef.current) {
        try {
          await rtcClientRef.current.leave();
        } catch (_) {}
      }
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const minimizeCall = useCallback(() => {
    setCallSession((prev) => (prev ? { ...prev, isMinimized: true } : prev));
  }, []);

  const expandCall = useCallback(() => {
    setCallSession((prev) => (prev ? { ...prev, isMinimized: false } : prev));
  }, []);

  const updateSessionToken = useCallback((partial) => {
    setCallSession((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...partial };
      if (partial?.serverNow) {
        const serverNowMs = new Date(partial.serverNow).getTime();
        if (Number.isFinite(serverNowMs)) {
          next.serverOffsetMs = serverNowMs - Date.now();
        }
      }
      return next;
    });
  }, []);

  const onAutoEndNavigate = useCallback(() => {
    try {
      navigateAwayRef.current?.();
    } catch (_) {}
  }, []);

  const registerNavigateAway = useCallback((fn) => {
    navigateAwayRef.current = fn;
  }, []);

  const appointmentPath = callSession?.appointmentId
    ? `/courses/detail/${callSession.appointmentId}`
    : null;

  // Any navigation away from the full call page (dashboard, courses, etc.)
  // must show the floating PiP immediately; returning to the call page expands it.
  const isOnCallPage = location.pathname === CALL_CHAT_PATH;
  const hasActiveCall = Boolean(callSession?.channel);

  useEffect(() => {
    if (!hasActiveCall) return;
    if (isOnCallPage) {
      setCallSession((prev) => (prev && prev.isMinimized ? { ...prev, isMinimized: false } : prev));
    } else {
      setCallSession((prev) => (prev && !prev.isMinimized ? { ...prev, isMinimized: true } : prev));
    }
  }, [hasActiveCall, isOnCallPage]);

  const contextValue = useMemo(
    () => ({
      callSession,
      startCall,
      endCall,
      minimizeCall,
      expandCall,
      appointmentPath,
      registerNavigateAway,
    }),
    [callSession, startCall, endCall, minimizeCall, expandCall, appointmentPath, registerNavigateAway],
  );

  if (!callSession || !rtcClientRef.current) {
    return (
      <CallSessionContext.Provider value={contextValue}>
        {children}
      </CallSessionContext.Provider>
    );
  }

  return (
    <CallSessionContext.Provider value={contextValue}>
      <AgoraRTCProvider client={rtcClientRef.current}>
        <CallMediaEngine
          session={callSession}
          updateSessionToken={updateSessionToken}
          onSessionEnd={clearSession}
          onAutoEndNavigate={onAutoEndNavigate}
          teardownRef={teardownRef}
        >
          {children}
          {callSession.isMinimized ? <FloatingCallOverlay /> : null}
        </CallMediaEngine>
      </AgoraRTCProvider>
    </CallSessionContext.Provider>
  );
};
